import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import autocompleteService from '../../api/services/autocomplete';
import { AddressSelection } from '../../types/AddressSelection';
import { typedAddressMatch } from '../../utils/addressSearchMatch';

const DEBOUNCE_DELAY = 0; // Delay in milliseconds

interface Props {
  handleChange: (e: any) => void;
  handleKeyDown?: (e: any) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  property: AddressSelection | null;
  allowCities?: boolean;
  /** Called with the text in the field as it changes, selected or typed. */
  onInputValueChange?: (value: string) => void;
  /**
   * Keep a pasted address exactly as pasted instead of trimming it to the
   * street line, for fields where a unit number or city must not be lost.
   */
  keepFullPastedAddress?: boolean;
  /**
   * Pick the one suggestion the buyer has typed out through the state, so
   * typing a whole address works without tapping the list.
   */
  selectTypedMatch?: boolean;
}

const AutoComplete: React.FC<Props> = ({
  handleChange,
  handleKeyDown,
  placeholder,
  size,
  property,
  allowCities,
  onInputValueChange,
  keepFullPastedAddress,
  selectTypedMatch,
}) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(property?.address || '');
  const [loading, setLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [typingStartTime, setTypingStartTime] = useState<number | null>(null);
  const [hasSelectedOption, setHasSelectedOption] = useState(false);
  const [isPastedContent, setIsPastedContent] = useState(false);

  const filter = createFilterOptions<any>();

  // Helper function to extract street address from full address string
  const extractStreetAddress = (fullAddress: string): string => {
    // Remove extra whitespace and trim
    let cleaned = fullAddress.trim().replace(/\s+/g, ' ');

    // 1. Remove apartment/unit/suite numbers (but keep the main address)
    cleaned = cleaned.replace(
      /[,\s]+(apt|apartment|unit|suite|ste|#)\s*\w+/gi,
      ''
    );

    // 2. Handle multi-line addresses (take everything before newline)
    const lines = cleaned.split(/[\n\r]+/);
    if (lines.length > 0) {
      cleaned = lines[0].trim();
    }

    // 3. Parse comma-separated parts intelligently
    const parts = cleaned.split(',').map((p) => p.trim());

    if (parts.length > 1) {
      // Keep street address and potentially city/state
      let result = parts[0]; // Always keep street address

      // Check if second part looks like a city (not a country or zip)
      if (
        parts[1] &&
        !parts[1].match(/^(usa|united states|us)$/i) &&
        !parts[1].match(/^\d{5}/)
      ) {
        // Check if it contains state abbreviation
        const statePattern =
          // eslint-disable-next-line max-len
          /\b(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY)\b/i;

        // If part 2 has state, keep it
        if (parts[1].match(statePattern)) {
          result = result + ', ' + parts[1];
        }
        // If part 2 is city and part 3 is state, keep both
        else if (parts[2] && parts[2].match(statePattern)) {
          result = result + ', ' + parts[1] + ', ' + parts[2].substring(0, 2);
        }
      }

      cleaned = result;
    }

    // 4. Final cleanup - remove country and zip if at the end
    cleaned = cleaned.replace(/[,\s]+(usa|united states|us)$/gi, '');
    cleaned = cleaned.replace(/[,\s]+\d{5}(-\d{4})?$/g, '');

    return cleaned;
  };

  // Helper function to detect if content was likely pasted
  const detectPaste = (oldValue: string, newValue: string): boolean => {
    // If the change is more than 5 characters at once, it's likely a paste
    // Or if it contains commas or newlines (common in copied addresses)
    const largeDelta = Math.abs(newValue.length - oldValue.length) > 5;
    const hasFormatting = /[,\n\r]/.test(newValue);
    const hasFullAddress = /\d+.*[,\s]+[A-Z][a-z]+[,\s]+[A-Z]{2}/.test(
      newValue
    ); // e.g., "123 Main St, City, ST"

    return largeDelta || hasFormatting || hasFullAddress;
  };

  // Helper function to check if input looks like an address
  const looksLikeAddress = (input: string): boolean => {
    const trimmed = input.trim();
    // Must start with a number (street number)
    const startsWithNumber = /^\d+\s+/.test(trimmed);
    if (!startsWithNumber) return false;

    // Split by spaces and filter out empty parts
    const parts = trimmed.split(/\s+/).filter((p) => p.length > 0);

    // Standard address format: number + street name + street type
    // e.g., "123 Main Street", "456 Oak Ave", "789 First Road"
    if (parts.length < 3) {
      // Exception: Allow 2 parts only for well-known single-name streets
      // like "123 Broadway" or "456 Main" but name must be substantial
      if (parts.length === 2) {
        return parts[1].length >= 5; // e.g., "Broadway", "Market", "First"
      }
      return false;
    }

    // Second part should be a meaningful street name (not just a single letter)
    if (parts[1].length < 2) return false;

    // Common street types to validate (optional check)
    const commonStreetTypes = [
      'st',
      'street',
      'ave',
      'avenue',
      'rd',
      'road',
      'blvd',
      'boulevard',
      'dr',
      'drive',
      'ct',
      'court',
      'pl',
      'place',
      'ln',
      'lane',
      'way',
      'pkwy',
      'parkway',
      'cir',
      'circle',
    ];

    // If we have 3+ parts, it's likely a valid address
    // Could optionally check if last part matches common street types

    return true;
  };

  // Helper function to determine if we should show the fallback option
  const shouldShowFallback = (
    inputVal: string,
    hasExactMatch: boolean,
    optionsLength: number
  ): boolean => {
    if (!inputVal || hasExactMatch) return false;

    // First check: input must look like a valid address
    if (!looksLikeAddress(inputVal)) return false;

    const timeSinceTyping = typingStartTime ? Date.now() - typingStartTime : 0;

    // More lenient for pasted content
    if (isPastedContent && searchAttempted) {
      // Show fallback faster for pasted addresses that don't match
      return (
        (failedAttempts >= 2 && optionsLength === 0) ||
        (timeSinceTyping > 2000 && optionsLength === 0)
      );
    }

    // Regular typing: Show fallback only if input looks like address AND:
    // 1. User has made 3+ failed attempts with no results
    // 2. User has been typing for 5+ seconds without selecting and no results
    // 3. Strong address pattern with zero results after multiple attempts
    return (
      searchAttempted &&
      ((failedAttempts >= 3 && optionsLength === 0) ||
        (timeSinceTyping > 5000 && !hasSelectedOption && optionsLength === 0) ||
        (failedAttempts >= 2 &&
          optionsLength === 0 &&
          inputVal.split(/\s+/).length >= 3))
    );
  };

  useEffect(() => {
    if (property === null) {
      setInputValue('');
      setFailedAttempts(0);
      setTypingStartTime(null);
      setHasSelectedOption(false);
    }
  }, [property]);

  let placeholderStr = 'Address';
  if (placeholder) {
    placeholderStr = placeholder;
  }

  useEffect(() => {
    if (inputValue.length < 3) {
      setOptions([]);
      setLoading(false);
      setSearchAttempted(false);
      return;
    }
    setSearchAttempted(true);

    // Track when user starts typing (if not already tracked)
    if (!typingStartTime && inputValue.length >= 3) {
      setTypingStartTime(Date.now());
    }

    setLoading(true);
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await autocompleteService.find(inputValue);
        const data = res
          .filter((opt: any) => {
            if (allowCities) {
              return opt.searchType === 'A' || opt.searchType === 'C';
            }
            return opt.searchType === 'A';
          })
          .map((opt: any) => ({
            ...opt,
          }));

        setOptions(data);

        // Track failed attempts (only when we get NO results and input looks like address)
        if (data.length === 0 && looksLikeAddress(inputValue)) {
          setFailedAttempts((prev) => prev + 1);
        } else if (data.length > 0) {
          // Reset failed attempts when we get any results
          setFailedAttempts(0);
        }
      } catch (e) {
        console.error(e);
        // Also count API errors as failed attempts
        setFailedAttempts((prev) => prev + 1);
      }
      setLoading(false);
    }, DEBOUNCE_DELAY);
    setDebounceTimeout(timeout);

    // Don't automatically call handleChange here - only when user explicitly selects
    // This was causing the selected address to be cleared
    
    return () => clearTimeout(timeout); // Cleanup on component unmount or input change
  }, [inputValue]);

  useEffect(() => {
    if (!selectTypedMatch || property || hasSelectedOption) return;
    const match = typedAddressMatch(inputValue, options);
    if (match) {
      setHasSelectedOption(true);
      handleChange(match);
      // Close the list and the phone keyboard so the next step is on screen.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }, [options, inputValue]);

  return (
    <Autocomplete
      freeSolo
      fullWidth
      inputValue={inputValue}
      options={options}
      loading={loading}
      filterOptions={(opts, params) => {
        // Don't use MUI's default filtering - we want to show all results from backend
        // The backend already did the smart searching with variations
        const normalize = (str: string) =>
          (str || '')
            .replace(/,/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
        const inputValue = params.inputValue;
        const hasExactMatch = opts.some(
          (opt: any) =>
            normalize(opt.title) === normalize(inputValue) ||
            normalize(opt.address) === normalize(inputValue)
        );

        // Show all options from backend (they're already filtered by our smart search)
        const filtered = [...opts];

        // Only show fallback option when it makes sense
        if (shouldShowFallback(inputValue, hasExactMatch, opts.length)) {
          filtered.push({
            inputValue,
            title: `Use: "${inputValue}"`,
            subtitle: 'Address not found - click to use as entered',
            isNew: true,
          });
        }
        return filtered;
      }}
      onInputChange={(_, newInputValue, reason) => {
        // Detect if this looks like a paste
        const wasPasted = detectPaste(inputValue, newInputValue);

        if (wasPasted) {
          // Clean the pasted address to just the street portion
          const cleanedAddress = keepFullPastedAddress
            ? newInputValue.trim().replace(/\s+/g, ' ')
            : extractStreetAddress(newInputValue);
          setInputValue(cleanedAddress);
          onInputValueChange?.(cleanedAddress);
          setIsPastedContent(true);

          // For pasted content, show fallback sooner if needed
          setFailedAttempts(1); // Start with 1 to show fallback faster
        } else {
          setInputValue(newInputValue);
          onInputValueChange?.(newInputValue);
          setIsPastedContent(false);
        }

        // Reset tracking when user clears the input
        if (!newInputValue || newInputValue.length < 3) {
          setFailedAttempts(0);
          setTypingStartTime(null);
          setHasSelectedOption(false);
          setSearchAttempted(false);
          setIsPastedContent(false);
        }
      }}
      onChange={(_, value) => {
        if (value) {
          setHasSelectedOption(true);
          setFailedAttempts(0);
          setTypingStartTime(null);

          if ((value as any).isNew) {
            handleChange({
              address: value.inputValue,
              title: value.inputValue,
              searchType: 'A',
            });
          } else {
            handleChange(value);
          }
        } else {
          handleChange(value);
        }
      }}
      onKeyDown={handleKeyDown}
      getOptionLabel={(option: any) =>
        typeof option === 'string'
          ? option
          : option.inputValue
          ? option.inputValue
          : option.title || ''
      }
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.inputValue || option.id}
          style={{
            ...(option.isNew && {
              fontStyle: 'italic',
              opacity: 0.7,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#fafafa',
            }),
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{option.title}</span>
            {option.subtitle && (
              <span style={{ fontSize: '0.85em', opacity: 0.6 }}>
                {option.subtitle}
              </span>
            )}
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholderStr}
          variant="outlined"
          size={size}
          sx={{
            '& fieldset': {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
          data-testid="address-autocomplete"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AutoComplete;
